import React, { useEffect, useState } from 'react';
import {
  Button,
  ListGroup,
  Modal,
  Form,
  Card,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
} from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'react-bootstrap-icons';

const ProjectsTab = ({ user }) => {
  const [category, setCategory] = useState('');
  const [startDate, setStartDate] = useState('');
  const [projects, setProjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [technologies, setTechnologies] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [viewMode, setViewMode] = useState('all');
  const [alertMessage, setAlertMessage] = useState('');
  const [userApplications, setUserApplications] = useState([]);
  const [editProjectId, setEditProjectId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const API_URL = process.env.REACT_APP_API_URL;

  const fetchProjects = async () => {
    try {
      const endpoint =
        user?.role === 'B2B' && viewMode === 'mine'
          ? `/private/api/projects/${user._id}`
          : `/private/api/projects/all`;

      const res = await fetch(API_URL + endpoint);
      const data = await res.json();
      setProjects(data);

      // Collect applications
      const appliedProjectIds = data
        .filter(p => p.applicants?.some(a => a.userId === user._id))
        .map(p => p._id);
      setUserApplications(appliedProjectIds);

    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  };

  useEffect(() => {
    if (user?.role === 'Freelancer') {
      setViewMode('all');
    }
  }, [user]);

  useEffect(() => {
    if (user) fetchProjects();
  }, [viewMode, user]);

  useEffect(() => {
    const url = new URLSearchParams(location.search);
    const projectId = url.get('projectId');
    if (projectId) {
      const project = projects.find((p) => p._id === projectId);
      if (project) setSelectedProject(project);
    }
  }, [location.search, projects]);

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    navigate(`?tab=projects&projectId=${project._id}`);
  };

  const handleBack = () => {
    setSelectedProject(null);
    navigate(`?tab=projects`);
  };

  // CREATE
  const handleSubmit = async () => {
    await fetch(API_URL + '/private/api/projects', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title,
        description,
        technologies,
        category,
        start_date: startDate,
        b2bId: user._id,
      }),
    });
    setShowModal(false);
    setTitle('');
    setDescription('');
    setTechnologies('');
    setCategory('');
    setStartDate('');
    fetchProjects();
  };

  // UPDATE
  const handleEdit = (project) => {
    setEditProjectId(project._id);
    setTitle(project.title);
    setDescription(project.description);
    setTechnologies(Array.isArray(project.technologies) ? project.technologies.join(', ') : project.technologies || '');
    setCategory(Array.isArray(project.category) ? project.category.join(', ') : project.category || '');
    setStartDate(project.start_date ? new Date(project.start_date).toISOString().substr(0,10) : '');
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    await fetch(`${API_URL}/private/api/projects/${editProjectId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title,
        description,
        technologies,
        category,
        start_date: startDate,
      }),
    });
    setShowEditModal(false);
    setEditProjectId(null);
    setTitle('');
    setDescription('');
    setTechnologies('');
    setCategory('');
    setStartDate('');
    fetchProjects();
    if (selectedProject && selectedProject._id === editProjectId) {
      setSelectedProject({
        ...selectedProject,
        title,
        description,
        technologies: technologies.split(',').map(s=>s.trim()),
        category: category.split(',').map(s=>s.trim()),
        start_date: startDate
      });
    }
  };

  // DELETE
  const handleDelete = async () => {
    if (!selectedProject?._id) return;
    await fetch(`${API_URL}/private/api/projects/${selectedProject._id}`, { method: 'DELETE' });
    setShowDeleteModal(false);
    setSelectedProject(null);
    fetchProjects();
  };

  // APPLY
  const handleApply = async (projectId, projectTitle) => {
    try {
      const res = await fetch(`${API_URL}/private/api/projects/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user._id,
          role: user.role,
          name: `${user.first_name} ${user.last_name}`,
          projectId,
          projectTitle
        })
      });

      const data = await res.json();
      if (res.ok) {
        setAlertMessage('Successfully applied!');
        fetchProjects();
      } else {
        setAlertMessage(data.message || 'Failed to apply.');
      }
    } catch (err) {
      console.error('Apply error:', err);
      setAlertMessage('Something went wrong.');
    }
  };

  return (
    <div>
      {user?.role !== 'Freelancer' && !selectedProject && (
        <div className="mb-3 d-flex gap-2 align-items-center">
          <strong>View:</strong>
          <ToggleButtonGroup
            type="radio"
            name="viewMode"
            value={viewMode}
            onChange={(val) => setViewMode(val)}
          >
            <ToggleButton id="view-all" value="all" variant={viewMode === 'all' ? 'primary' : 'outline-primary'}>
              All Projects
            </ToggleButton>
            <ToggleButton id="view-mine" value="mine" variant={viewMode === 'mine' ? 'primary' : 'outline-primary'}>
              My Projects
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      )}

      {!selectedProject && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4>{user?.role !== 'B2B' ? 'Available Projects' : 'Projects'}</h4>
          {user?.role === 'B2B' && <Button onClick={() => setShowModal(true)}>+ Create New Project</Button>}
        </div>
      )}

      {selectedProject && (
        <>
          <Button variant="link" className="mb-3 back" onClick={handleBack}>
            <ArrowLeft /> Back to Projects
          </Button>

          <Card className="p-3 mb-3 shadow-sm" id='project-details'>
            <h5>{selectedProject.title}</h5>
            <div>
              <strong>Description:</strong>
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  fontFamily: "inherit",
                  background: "none",
                  border: "none",
                  margin: 0,
                  padding: 0,
                  fontSize: "1rem"
                }}
              >
                {selectedProject.description}
              </pre>
            </div>

            <p><strong>Technologies:</strong> {Array.isArray(selectedProject.technologies) ? selectedProject.technologies.join(', ') : selectedProject.technologies}</p>
            <p><strong>Category:</strong>{' '}
              {Array.isArray(selectedProject.category) ? (
                selectedProject.category.map((cat, i) => (
                  <span key={i} className="badge bg-info text-dark me-2">{cat}</span>
                ))
              ) : (
                <span className="badge bg-info text-dark">{selectedProject.category}</span>
              )}
            </p>
            <p><strong>Start Date:</strong> {selectedProject.start_date ? new Date(selectedProject.start_date).toLocaleDateString() : 'N/A'}</p>
            {['Freelancer', 'B2B'].includes(user?.role) &&
              selectedProject.b2bId !== user._id && (
                userApplications.includes(selectedProject._id) ? (
                  <Alert variant="info">You have already applied to this project.</Alert>
                ) : (
                  <Button onClick={() => handleApply(selectedProject._id, selectedProject.title)}>
                    Apply
                  </Button>
                )
              )
            }

            {user?.role === 'B2B' && selectedProject.b2bId === user._id && (
              <div className="mt-3 d-flex gap-2">
                <Button variant="warning" onClick={() => handleEdit(selectedProject)}>Edit</Button>
                <Button variant="danger" onClick={() => setShowDeleteModal(true)}>Delete</Button>
              </div>
            )}
          </Card>

          {alertMessage && <Alert onClose={() => setAlertMessage('')} dismissible>{alertMessage}</Alert>}
        </>
      )}

      {!selectedProject && projects.length === 0 ? (
        <div>No projects available at the moment.</div>
      ) : !selectedProject && (
        <ListGroup>
          {projects.map((p) => (
            <ListGroup.Item
              key={p._id}
              action
              onClick={() => handleProjectClick(p)}
              className="d-flex justify-content-between align-items-center"
            >
              <div>
                <strong>{p.title}</strong>
                <div className="text-muted" style={{ fontSize: '0.9em' }}>
                  {Array.isArray(p.category) ? p.category.join(', ') : (p.category || 'No Category')}
                </div>
              </div>
              <div className="text-end">
                <small className="text-muted d-block">
                  {p.start_date ? new Date(p.start_date).toLocaleDateString() : 'No Date'}
                </small>
                {userApplications.includes(p._id) && <span className="badge bg-success">Applied</span>}
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}

      {/* CREATE MODAL */}
      {user?.role === 'B2B' && (
        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Create Project</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control value={title} onChange={(e) => setTitle(e.target.value)} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Form.Control value={category} onChange={(e) => setCategory(e.target.value)} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Start Date</Form.Label>
                <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control as="textarea" value={description} onChange={(e) => setDescription(e.target.value)} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Technologies</Form.Label>
                <Form.Control value={technologies} onChange={(e) => setTechnologies(e.target.value)} />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={handleSubmit}>Save</Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* EDIT MODAL */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control value={title} onChange={(e) => setTitle(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Control value={category} onChange={(e) => setCategory(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Start Date</Form.Label>
              <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" value={description} onChange={(e) => setDescription(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Technologies</Form.Label>
              <Form.Control value={technologies} onChange={(e) => setTechnologies(e.target.value)} />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleUpdate}>Update</Button>
        </Modal.Footer>
      </Modal>

      {/* DELETE MODAL */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this project?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProjectsTab;
